import React, { useMemo, useState } from "react";

import { ICosignUnmappedProperty, useCosignPropertyMappings } from "manager/hooks/api";
import { NOTIFICATIONS } from "shared/config/constants";
import { BackLinkProps } from "shared/interfaces/misc";
import { openNotification } from "shared/utils/ui";

import CreateCosignPropertyModal from "../CreateCosignPropertyModal";
import MapExistingCosignPropertyModal from "../MapExistingCosignPropertyModal";

import CosignDashboard from "./CosignDashboard";
import { useCosignDisconnectAction } from "./useCosignDisconnectAction";
import { useCosignToggleAction } from "./useCosignToggleAction";

interface CosignDashboardContainerProps {
  backLink?: BackLinkProps;
}

const filterUnmappedProperties = (
  properties: ICosignUnmappedProperty[],
  searchTerm: string
): ICosignUnmappedProperty[] => {
  const query = searchTerm.trim().toLowerCase();
  if (!query) {
    return properties;
  }

  return properties.filter(
    (property) =>
      property.name.toLowerCase().includes(query) ||
      property.fullAddress.toLowerCase().includes(query)
  );
};

const CosignDashboardContainer: React.FC<CosignDashboardContainerProps> = ({
  backLink,
}) => {
  const [unmappedSearchTerm, setUnmappedSearchTerm] = useState("");
  const [createProperty, setCreateProperty] =
    useState<ICosignUnmappedProperty | null>(null);
  const [mapProperty, setMapProperty] =
    useState<ICosignUnmappedProperty | null>(null);

  const {
    cosignPropertyMappings,
    isCosignPropertyMappingsLoading,
    cosignPropertyMappingsError,
    refetchCosignPropertyMappings,
  } = useCosignPropertyMappings();

  const { disconnectProperty, isDisconnectingCosignPropertyMapping } =
    useCosignDisconnectAction();

  const { toggleProperty, isUpdatingCosignPropertyIsActive } =
    useCosignToggleAction();

  const filteredUnmappedProperties = useMemo(
    () =>
      filterUnmappedProperties(
        cosignPropertyMappings?.otherVeroProperties ?? [],
        unmappedSearchTerm
      ),
    [cosignPropertyMappings?.otherVeroProperties, unmappedSearchTerm]
  );

  const onMapToExisting = (property: ICosignUnmappedProperty) => {
    setMapProperty(property);
  };

  const onCreateInCosign = (property: ICosignUnmappedProperty) => {
    setCreateProperty(property);
  };

  const closeCreateModal = () => setCreateProperty(null);
  const closeMapModal = () => setMapProperty(null);

  const onPropertyCreated = () => {
    closeCreateModal();
    refetchCosignPropertyMappings();
    openNotification(
      "Property created and mapped in Cosign.",
      NOTIFICATIONS.info
    );
  };

  const onPropertyMapped = async () => {
    await refetchCosignPropertyMappings();
    closeMapModal();
    openNotification(
      "Property mapped to existing Cosign property.",
      NOTIFICATIONS.info
    );
  };

  return (
    <CosignDashboard
      backLink={backLink}
      mappings={cosignPropertyMappings}
      isLoading={isCosignPropertyMappingsLoading}
      hasError={Boolean(cosignPropertyMappingsError && !cosignPropertyMappings)}
      onRetry={() => refetchCosignPropertyMappings()}
      unmappedSearchTerm={unmappedSearchTerm}
      onUnmappedSearchChange={setUnmappedSearchTerm}
      filteredUnmappedProperties={filteredUnmappedProperties}
      onDisconnect={disconnectProperty}
      onToggle={toggleProperty}
      isToggleUpdating={
        isUpdatingCosignPropertyIsActive || isDisconnectingCosignPropertyMapping
      }
      onMapToExisting={onMapToExisting}
      onCreateInCosign={onCreateInCosign}
      createModal={
        <>
          {createProperty ? (
            <CreateCosignPropertyModal
              open
              onClose={closeCreateModal}
              onCreated={onPropertyCreated}
              veroProperty={{
                id: String(createProperty.veroPropertyId),
                name: createProperty.name,
                address: createProperty.address,
                city: createProperty.city,
                state: createProperty.state,
                zipCode: createProperty.zipCode,
                numberOfUnits: createProperty.numberOfUnits,
              }}
            />
          ) : null}
          {mapProperty ? (
            <MapExistingCosignPropertyModal
              open
              onCancel={closeMapModal}
              onSuccess={onPropertyMapped}
              property={mapProperty}
            />
          ) : null}
        </>
      }
    />
  );
};

export default CosignDashboardContainer;
